import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import * as Scroll from 'react-scroll';
import { addMonths, isSameDay, getDate, getMonth, differenceInMonths, e4Format, e4Formats, makeDate } from 'helpers/dates';
import { Modal, SaveButton, SystemText } from 'e4shared';
import Files from 'components/admin/forms/files';
import { apiGet, apiPost } from 'services';
import './linkedin.css';
import { uploadType } from 'helpers/azure';

export const CheckLinkedInFileName = file => {
    const st = (path, ...rest) => SystemText(`Admin/Integrations/LinkedIn/${path}`, ...rest);

    const pattern = new RegExp(/Sales_Navigator_Usage_Reporting_(\d{4})-(\d{2})-(\d{2})_to_(\d{4})-(\d{2})-(\d{2}).csv/);
    if (!pattern.test(file.name)) {
        return st('Fields/File/Errors/Pattern');
    }

    try {
        const start = makeDate(file.name.substring(32, 42));
        const end = makeDate(file.name.substring(46, 56));

        if (isNaN(getMonth(start)) || isNaN(getMonth(end))) {
            return st('Fields/File/Errors/Parsing');
        }

        if (getMonth(start) !== getMonth(end)) {
            return st('Fields/File/Errors/SameMonth');
        }

        if (getDate(start) !== 1) {
            return st('Fields/File/Errors/NotFirst');
        }

        if (differenceInMonths(new Date(), start) > 12) {
            return st('Fields/File/Errors/TooOld');
        }

        return '';
    } catch {
        return st('Fields/File/Errors/Parsing');
    }
};

export const LinkedIn = ({ show, close, reload }) => {
    const [uploads, setUploads] = useState([]);
    const [refresh, setRefresh] = useState(0);
    const [refreshing, setRefreshing] = useState(true);

    const guid = useSelector(state => state.user.user.guid);

    const st = (path, ...rest) => SystemText(`Admin/Integrations/LinkedIn/${path}`, ...rest);

    React.useEffect(() => {
        const handleResponse = response => {
            setUploads(response);
            setRefreshing(false);
        };
        show && apiGet(`linkedin-files`, guid, response => handleResponse(response));
    }, [show, guid, refresh]);

    const save = files => {
        const body = files[files.length - 1];

        // Needs to wait for file to be saved from azure upload
        setTimeout(() => {
            apiPost('linkedin-prepare', body, guid, () => {
                setRefresh(refresh + 1);
            });
        }, 1000);
    };

    const closeModal = () => {
        reload?.();
        close?.();
    };

    const months = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
    const date = new Date();
    const firstOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);

    return (
        <Modal show={show} title={st('Fields/Manage/Label')} close={closeModal}>
            <Scroll.Element name="top-of-modal" />
            <div className="row linkedin-key">
                <div className="col-md-3">
                    <i className="fa fa-check-circle text-success mr5" />
                    {st('Manage/Uploaded')}
                </div>
                <div className="col-md-3">
                    <i className="fa fa-warning text-warning mr5" />
                    {st('Manage/Partial')}
                </div>
                <div className="col-md-3">
                    <i className="fa fa-ban text-danger mr5" />
                    {st('Manage/Missing')}
                </div>
            </div>
            <label>{st('Manage/Last12')}</label>
            <p>{st('Manage/Check')}</p>
            <div className="mb20">
                {months.map(m => {
                    const month = addMonths(firstOfMonth, m * -1);
                    const uploadMonth = uploads?.filter(u => isSameDay(u.start_Date, month));
                    const current = uploadMonth.length > 0 ? uploadMonth[0] : { status: 0 };
                    return (
                        <div className="flex flex-center mb10" key={`month-${m}`}>
                            <i className="fa fa-file mr10" />
                            <span className="text-lg mr10">{e4Format(month, e4Formats.monthLong)}</span>
                            {current.status === 3 ? (
                                <i
                                    className="fa fa-check-circle text-success"
                                    title={st('Manage/Uploaded/Info', e4Format(date, e4Formats.default, true))}
                                />
                            ) : current.status === 99 ? (
                                <i
                                    className="fa fa-exclamation text-danger"
                                    title={st(
                                        'Fields/File/Errors/Info',
                                        current.error ? current.error : st('Fields/File/Errors/Info/Default')
                                    )}
                                />
                            ) : current.status === 4 ? (
                                <i
                                    className="fa fa-warning text-warning"
                                    title={st('Manage/Partial/Info', e4Format(current.end_Date, e4Formats.monthLong))}
                                />
                            ) : current.status === 1 || current.status === 2 ? (
                                <i className="fa fa-spin fa-spinner" title={st('Manage/Processing')} />
                            ) : (
                                <i className="fa fa-ban text-danger" title={st('Manage/NoData')} />
                            )}
                        </div>
                    );
                })}
                <SaveButton
                    saving={refreshing}
                    setSaving={() => setRefreshing(true)}
                    onClick={() => setRefresh(refresh + 1)}
                    className="btn btn-success btn-sm"
                >
                    {st('Buttons/Refresh')}
                </SaveButton>
            </div>
            <div>
                <Files
                    label={st('Fields/File/Label')}
                    folder={uploadType.linkedIn}
                    onChange={save}
                    extensions={['.csv']}
                    hideDelete
                    fileValidation={CheckLinkedInFileName}
                    allowedSources={['file']}
                    allowMultiple
                    help={st('Fields/File/Help')}
                />
            </div>
        </Modal>
    );
};

export default LinkedIn;
